/**
 * Authentication Service
 * Handles admin user authentication
 * Mock/Real switching is handled at Service layer
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { UserService } from '../user/UserService';
import { delay } from '../mock/mockData';
import type {
  LoginCredentials,
  AdminAuthUser,
  LoginResponseData,
  AuthResponse,
} from '../../types/auth';

class AuthService {
  /**
   * Login with email and password
   */
  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      // Determine role based on email for mock mode
      let role: 'super_admin' | 'support' = 'support';
      if (credentials.email.toLowerCase().includes('superadmin') || credentials.email.toLowerCase() === 'superadmin@mispy.ai') {
        role = 'super_admin';
      } else if (credentials.email.toLowerCase().includes('support') || credentials.email.toLowerCase() === 'support@mispy.ai') {
        role = 'support';
      }

      // Mock login - accept any email/password for demo
      const authResponse: AuthResponse = {
        user: {
          id: role === 'super_admin' ? 'admin-001' : 'admin-002',
          adminId: role === 'super_admin' ? 'MS-84321' : 'MS-84322',
          name: role === 'super_admin' ? 'Super Admin User' : 'Support User',
          email: credentials.email,
          role,
        },
        tokens: {
          accessToken: 'mock-access-token',
          refreshToken: 'mock-refresh-token',
          expiresAt: "2026-01-06T08:19:34.5407232+00:00",
        },
      };

      // Store tokens and user in mock mode too
      localStorage.setItem('accessToken', authResponse.tokens.accessToken);
      localStorage.setItem('refreshToken', authResponse.tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(authResponse.user));

      return authResponse;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        data: LoginResponseData;
        message?: string;
      }>('/admin/auth/login', credentials);

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Login failed');
      }

      const { email, role: apiRole, accessToken, refreshToken, expiresAt } = response.data as unknown as LoginResponseData;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('expiresAt', expiresAt);

      // Map API role to internal AdminAuthUser role
      let role: AdminAuthUser['role'] = 'support'; // default
      const normalizedApiRole = apiRole.toLowerCase();
      if (normalizedApiRole.includes('admin')) {
        role = 'super_admin';
      } else if (normalizedApiRole.includes('support')) {
        role = 'support';
      } else if (normalizedApiRole.includes('investigator')) {
        role = 'investigator';
      }

      // Get current user details
      let currentUser;
      try {
        currentUser = await UserService.getCurrentUser();
      } catch (error) {
        console.warn('Failed to fetch current user details after login:', error);
        // Continue with login even if user details fetch fails
      }

      // Store tokens
      const userData = {
        id: currentUser?.id,
        name: currentUser?.firstName && currentUser?.lastName
          ? `${currentUser.firstName} ${currentUser.lastName}`
          : 'Admin User',
        email,
        role,
      };

      localStorage.setItem('user', JSON.stringify(userData));

      return {
        user: userData,
        tokens: {
          accessToken,
          refreshToken,
          expiresAt,
        },
      };
    }
  }

  /**
   * Logout
   */
  static async logout(): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY / 2);
    } else {
      try {
        await apiService.post('/auth/logout');
      } catch (error) {
        // Even if API call fails, clear local storage
        console.error('Logout API call failed:', error);
      }
    }
    
    // Clear all authentication-related data from local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('user');
    
    // Dispatch a custom event to notify other components of logout
    // This ensures Sidebar and other components update immediately
    window.dispatchEvent(new Event('storage'));
  }

  /**
   * Get current user from storage
   */
  static getCurrentUser(): AdminAuthUser | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }
}

export { AuthService };

